import { useEffect, useRef, useState, useCallback } from "react";

import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import Button from "@/components/button";
import Input from "@/components/input";
import { useAuth } from "@/context/auth";
import {
  useStartVerificationMutation,
  useConfirmVerificationMutation,
  useVerifyEmailByTokenMutation,
} from "@/queries/hooks/authHooks";
import { notify } from "@/utils";

export const Route = createFileRoute("/auth/_layout/email-confirmation")({
  beforeLoad: ({ context, search }) => {
    // Allow access via token even if not authenticated
    const hasToken = Boolean((search as any)?.token);
    if (!context.auth?.isAuthenticated && !hasToken) {
      throw redirect({ to: "/auth/login" });
    }
  },
  component: EmailConfirmationPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: search.token as string | undefined,
    };
  },
});

function EmailConfirmationPage() {
  const { t } = useTranslation("auth");
  const auth = useAuth();
  const navigate = useNavigate();
  const { token } = useSearch({ from: "/auth/_layout/email-confirmation" });

  const userEmail = auth.user?.email || "";
  const resendEmailVerificationMutation = useStartVerificationMutation("email");
  const confirmVerificationMutation = useConfirmVerificationMutation("email");
  const verifyEmailByTokenMutation = useVerifyEmailByTokenMutation();

  const [showManualInput, setShowManualInput] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [isAutoVerifying, setIsAutoVerifying] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Auto-verify email if token is present in URL (run once per token)
  const autoAttemptedRef = useRef(false);
  // Prevent info notification from showing multiple times
  const infoShownRef = useRef(false);

  const handleResendEmail = () => {
    setErrorMsg(null);
    setInfo(null);
    resendEmailVerificationMutation
      .mutateAsync()
      .then(() => {
        setInfo(t("emailConfirmation.linkSentSuccess"));
      })
      .catch((error: any) => {
        setErrorMsg(error.message || t("emailConfirmation.linkSendError"));
      });
  };

  const handleVerification = useCallback(
    async (code: string, isEmailToken = false) => {
      if (!code.trim()) {
        setErrorMsg(t("emailConfirmation.enterCodeError"));
        return;
      }

      try {
        if (isEmailToken) {
          // Email link click — public endpoint, no authentication required
          await verifyEmailByTokenMutation.mutateAsync({ token: code });
        } else {
          // Manual code entry — authenticated endpoint
          await confirmVerificationMutation.mutateAsync({ code });
        }
        setInfo(t("emailConfirmation.verifiedSuccess"));

        // If user is not authenticated, send them to login
        if (!auth.isAuthenticated) {
          navigate({ to: "/auth/login" });
          return;
        }

        auth.markEmailVerified();
        const userRole = auth.user?.role || "worker";

        // Check if phone is verified
        if (auth.user?.phoneVerified) {
          // Check if onboarding is completed
          if (auth.user.onboardingCompleted) {
            navigate({
              to: "/app/$role/dashboard",
              params: { role: userRole },
            });
          } else {
            navigate({
              to: "/app/$role/setup",
              params: { role: userRole },
            });
          }
        } else {
          // Go to phone verification
          navigate({ to: "/auth/verification" });
        }
      } catch (error: any) {
        const msg = error?.message || t("emailConfirmation.invalidOrExpired");
        setErrorMsg(msg);
      } finally {
        setIsAutoVerifying(false);
      }
    },
    [
      auth,
      navigate,
      setErrorMsg,
      confirmVerificationMutation,
      verifyEmailByTokenMutation,
      t,
    ],
  );

  useEffect(() => {
    if (!token) {
      if (!infoShownRef.current) {
        notify.info(t("emailConfirmation.infoOpenLink"));
        infoShownRef.current = true;
      }
      return;
    }
    if (autoAttemptedRef.current) return;
    autoAttemptedRef.current = true;
    setIsAutoVerifying(true);
    handleVerification(token, true).finally(() => setIsAutoVerifying(false));
  }, [token, handleVerification, t]);

  const handleManualCodeSubmit = async () => {
    await handleVerification(manualCode);
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="absolute top-0 right-0"
        onClick={auth.logout}
      >
        {t("verify.logout")}
      </Button>
      <h1 className="heading-1 mb-5">{t("emailConfirmation.title")}</h1>
      <img
        src="/email-2fa.svg"
        alt={t("emailConfirmation.imageAlt")}
        width={60}
        height={60}
        className="mb-6"
      />

      {isAutoVerifying ? (
        <div className="text-center">
          <p className="body mb-6">{t("emailConfirmation.verifyingEmail")}</p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
        </div>
      ) : (
        <>
          <p className="body mb-6">
            {t("emailConfirmation.linkSentTo")}
            <span className="underline">{userEmail}</span>
          </p>
          <p className="body">{t("emailConfirmation.checkEmailPrompt")}</p>
          {info && <p className="body-small text-green-600 mt-2">{info}</p>}
          {errorMsg && (
            <p className="body-small text-red-500 mt-2">{errorMsg}</p>
          )}
        </>
      )}
      <div className="border-[#200A0A]  w-full border mt-3" />

      <div className="flex flex-col gap-5 items-start">
        {!isAutoVerifying && (
          <>
            <Button
              onClick={handleResendEmail}
              variant="link"
              className="-ml-5 mt-2"
            >
              {t("verify.resendConfirmationEmail")}
            </Button>
            {!showManualInput ? (
              <Button
                variant="secondary"
                className="mt-20 w-[320px]"
                onClick={() => setShowManualInput(true)}
              >
                {t("emailConfirmation.enterCodeManually")}
              </Button>
            ) : (
              <div className="mt-20 w-[320px]">
                <Input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="one-time-code"
                  maxLength={8}
                  placeholder={t("emailConfirmation.codePlaceholder")}
                  value={manualCode}
                  onChange={(e) =>
                    setManualCode(
                      e.target.value.replace(/\s/g, "").toUpperCase(),
                    )
                  }
                  className=""
                />
                <Button
                  variant="primary"
                  className="w-full mb-2 mt-4"
                  onClick={handleManualCodeSubmit}
                >
                  {t("emailConfirmation.verifyCode")}
                </Button>
                <Button
                  variant="link"
                  className="text-white"
                  onClick={() => setShowManualInput(false)}
                >
                  {t("emailConfirmation.backToEmailLink")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
